package ai.wagemix.logic.sharedlinks

import ai.wagemix.logic.entitledPlan
import ai.wagemix.logic.lapsedFlow
import ai.wagemix.data.WMMixSyncEvent
import kotlinx.coroutines.flow.combine
import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.net.models.WMShareStats
import ai.wagemix.logic.share.shareStatsFlow
import ai.wagemix.data.touchesUser
import ai.wagemix.logic.WMSharingDataGateway
import ai.wagemix.logic.session
import ai.wagemix.logic.share.WMSharePreviewState
import ai.wagemix.logic.share.ownSharesFlow
import ai.wagemix.logic.share.previewStalesIn
import ai.wagemix.logic.share.previewState
import ai.wagemix.logic.sharedlinks.WMSharedLinksPresenter.Action
import ai.wagemix.logic.sharedlinks.WMSharedLinksPresenter.Event
import ai.wagemix.logic.sharedlinks.WMSharedLinksPresenter.Prompt
import ai.wagemix.net.models.WMBadge
import ai.wagemix.net.models.WMOwnedShare
import ai.wagemix.net.models.WMSharedMixPurpose
import com.fsryan.foundation.logic.FSPresenter
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.presenterInstanceId
import com.fsryan.foundation.logic.prompting.FSBasePromptingPresenter
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import com.fsryan.foundation.logic.prompting.defaultHandlePromptResponse
import com.fsryan.foundation.models.FSPropertyContainer
import kotlin.jvm.JvmInline
import kotlin.time.Clock
import kotlin.time.Instant
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.onStart

/** The data the shared links page needs: the user's mirrored shares, their click statistics, and the share syncs. */
interface WMSharedLinksDataGateway: WMSharingDataGateway

/** One of the user's shared links, as the shared links page lists it. */
data class WMSharedLinkRow(
    val sharedMixId: String,
    val title: String,
    val badge: WMBadge,
    val hourly: Boolean,
    val purpose: WMSharedMixPurpose,
    val shareableUrl: String,
    val createdAt: Instant?,
    val previewImageUrl: String?,
    val previewState: WMSharePreviewState,
    /** Whether the link works; the owner turns it off and on. */
    val active: Boolean,
    /** What the card says about how often the link was opened (planning/share-link-stats-plan.md rules 2, 7, 8). */
    val stats: WMSharedLinkStats,
)

/** A shared link's statistics as its card shows them. */
sealed interface WMSharedLinkStats {
    /** Pro: how often it was opened, how many of those while it was off, and when it was last opened. */
    data class Shown(
        val clickCount: Long,
        val deactivatedClickCount: Long,
        val lastClickedAt: Instant?
    ): WMSharedLinkStats
    /** Stored, but the plan is not Pro: the card offers Pro. */
    data object Locked: WMSharedLinkStats
    /** Shared before link statistics were added: there are none, on any plan. */
    data object Untracked: WMSharedLinkStats
    /**
     * Pro, but the statistics have not been read (still loading, or refused): the card shows none rather than zeros
     * it does not know.
     */
    data object Unavailable: WMSharedLinkStats
}

/**
 * The shared links page: every link the user has shared, newest first, with its preview image's state, regenerating a
 * preview that failed or stalled, and turning a link off and on. Pro subscribers also see how often each link was
 * opened; on any other plan a card whose statistics are stored offers Pro (planning/share-link-stats-plan.md).
 */
interface WMSharedLinksPresenter<P: Prompt, out E: Event<P>, in A: Action>: FSPromptingPresenter<P, E, A> {

    interface Action: FSPromptingPresenter.Action {
        sealed interface Base: Action

        @JvmInline
        value class ComposedSuperAction internal constructor(val wrapped: FSPromptingPresenter.Action.Base): Base

        @ConsistentCopyVisibility
        data class RequestRegeneratePreview internal constructor(val sharedMixId: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<RequestRegeneratePreview> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        /** The link's switch was flipped: [active] is the state the user asked for. */
        @ConsistentCopyVisibility
        data class SetActive internal constructor(val sharedMixId: String, val active: Boolean, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<SetActive> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        interface Factory<P: Prompt, out A: Action>: FSPromptingPresenter.Action.Factory<P, A> {
            fun createRequestRegeneratePreview(sharedMixId: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createSetActive(sharedMixId: String, active: Boolean, extendedProperties: Map<String, Any> = emptyMap()): A
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base> = object: Factory<P, Base> {
                private val base = FSPromptingPresenter.Action.baseFactory<P>()
                override fun createUserRespondedToPrompt(response: FSPrompt.Response<P>, extendedProperties: Map<String, Any>): Base = ComposedSuperAction(base.createUserRespondedToPrompt(response, extendedProperties))
                override fun createRequestRegeneratePreview(sharedMixId: String, extendedProperties: Map<String, Any>): Base = RequestRegeneratePreview(sharedMixId, extendedProperties)
                override fun createSetActive(sharedMixId: String, active: Boolean, extendedProperties: Map<String, Any>): Base = SetActive(sharedMixId, active, extendedProperties)
            }
        }
    }

    interface Event<out P: Prompt>: FSPromptingPresenter.Event<P> {
        /** The user's shared links, newest first; empty when they have shared nothing. */
        val rowsUpdate: List<WMSharedLinkRow>?
        /**
         * Whether the account has lapsed: the screen shows the renewal reminder instead of the links, which are
         * deactivated for viewers until renewal (planning/paid-feature-enforcement-plan.md W7, W8). `null` means no change.
         */
        val lapsedUpdate: Boolean?

        @ConsistentCopyVisibility
        data class Base<out P: Prompt> internal constructor(
            private val base: FSPromptingPresenter.Event.Base<P>,
            override val rowsUpdate: List<WMSharedLinkRow>? = null,
            override val lapsedUpdate: Boolean? = null,
        ): Event<P>, FSPropertyContainer<Base<P>>, FSPromptingPresenter.Event<P> by base {
            override val extendedProperties: Map<String, Any> get() = base.extendedProperties
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(base = base.replaceExtendedProperties(properties))
        }

        interface Factory<P: Prompt, out E: Event<P>>: FSPromptingPresenter.Event.Factory<P, E> {
            fun createRowsUpdate(rows: List<WMSharedLinkRow>): E
            fun createLapsedUpdate(lapsed: Boolean): E
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base<P>> = object: Factory<P, Base<P>> {
                private val base = FSPromptingPresenter.Event.baseFactory<P>()
                override fun createPromptChange(prompt: P, loading: Boolean?, extendedProperties: Map<String, Any>) = Base(base.createPromptChange(prompt, loading, extendedProperties))
                override fun createLoadingChange(loading: Boolean, extendedProperties: Map<String, Any>) = Base(base.createLoadingChange(loading, extendedProperties))
                override fun none() = Base(base.none())
                override fun createRowsUpdate(rows: List<WMSharedLinkRow>) = Base(base.none(), rowsUpdate = rows)
                override fun createLapsedUpdate(lapsed: Boolean) = Base(base.none(), lapsedUpdate = lapsed)
            }
        }
    }

    interface Prompt: FSPromptingPresenter.Prompt {
        sealed interface Base: Prompt

        @JvmInline
        value class ComposedSuperPrompt internal constructor(val wrapped: FSPromptingPresenter.Prompt.Base): Base

        /** Asking for a preview image again failed; the link itself is fine. */
        @ConsistentCopyVisibility
        data class PreviewRegenerationFailed internal constructor(val message: String?): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = message
            override val titleOverride: String? = null
            enum class Selection { Dismiss }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        /** Turning the link off (or on) failed; it stays as it was. */
        @ConsistentCopyVisibility
        data class ActivationFailed internal constructor(val active: Boolean, val message: String?): Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = message
            override val titleOverride: String? = null
            enum class Selection { Dismiss }
            fun selectionOf(selected: Int): Selection? = Selection.entries.getOrNull(selected)
        }

        interface Factory<out P: Prompt>: FSPromptingPresenter.Prompt.Factory<P> {
            fun previewRegenerationFailed(message: String?): P
            fun activationFailed(active: Boolean, message: String?): P
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                override fun createBase(wrapped: FSPrompt.Base): Base = ComposedSuperPrompt(FSPromptingPresenter.Prompt.baseFactory().createBase(wrapped))
                override fun previewRegenerationFailed(message: String?): Base = PreviewRegenerationFailed(message)
                override fun activationFailed(active: Boolean, message: String?): Base = ActivationFailed(active, message)
            }
        }
    }

    fun userRequestedRegeneratePreview(sharedMixId: String, extendedProperties: Map<String, Any> = emptyMap())
    /** Turns the link [sharedMixId] on ([active]) or off. */
    fun userChangedShareActive(sharedMixId: String, active: Boolean, extendedProperties: Map<String, Any> = emptyMap())

    companion object
}

abstract class WMBaseSharedLinksPresenter<
    P: Prompt,
    E: Event<P>,
    A: Action,
    EF: Event.Factory<P, E>,
    AF: Action.Factory<P, A>,
    PF: Prompt.Factory<P>,
    G: WMSharedLinksDataGateway
>(
    protected val dataGateway: G,
    eventFactory: EF,
    actionFactory: AF,
    promptFactory: PF,
    cancelViewScopeOnUnready: Boolean,
    instanceId: String,
    /** The time a pending preview is measured against, to tell when it has stalled. */
    protected val now: () -> Instant = { Clock.System.now() },
    handlePromptResponse: suspend (FSPrompt.Response<P>, suspend E.() -> Unit) -> Unit = { response, emit ->
        context(eventFactory, promptFactory, dataGateway) { defaultSharedLinksHandlePromptResponse(response, emit) }
    }
): FSBasePromptingPresenter<P, E, A, EF, AF, PF>(cancelViewScopeOnUnready, instanceId, eventFactory, actionFactory, promptFactory, handlePromptResponse),
    WMSharedLinksPresenter<P, E, A> {

    override fun userRequestedRegeneratePreview(sharedMixId: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRequestRegeneratePreview(sharedMixId, extendedProperties))
    override fun userChangedShareActive(sharedMixId: String, active: Boolean, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createSetActive(sharedMixId, active, extendedProperties))

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun registerSubscriptions() {
        dataGateway.lapsedFlow()
            .onEach { lapsed -> eventFactory.createLapsedUpdate(lapsed).emit() }
            .launchInViewScope()

        // The rows are the mirror of the user's shares, with their click statistics on Pro. A pending preview is looked
        // at again when it would stall, so a render that never reports back offers Regenerate without any new data.
        dataGateway.cacheUpdateFlow
            .filter { it.touchesUser() }
            .map { }
            .onStart { emit(Unit) }
            .map { dataGateway.session()?.uid to (dataGateway.entitledPlan() == WMSubscriptionType.Pro) }
            .distinctUntilChanged()
            .flatMapLatest { (uid, pro) ->
                combine(dataGateway.ownSharesFlow(uid), dataGateway.shareStatsFlow(uid)) { shares, stats ->
                    WMSharedLinksState(shares, stats.takeIf { pro }, pro)
                }
            }
            .flatMapLatest { state ->
                flow {
                    emit(state)
                    while (true) {
                        val next = state.shares?.mapNotNull { it.previewStalesIn(now()) }?.minOrNull() ?: break
                        delay(next)
                        emit(state)
                    }
                }
            }
            .onEach { state ->
                when (val shares = state.shares) {
                    null -> eventFactory.createLoadingChange(true).emit()   // the mirror has not arrived yet
                    else -> {
                        val stats = state.stats?.associateBy { it.sharedMixId }
                        eventFactory.createRowsUpdate(shares.map { it.toRow(now(), stats, state.statsVisible) }).emit()
                        eventFactory.createLoadingChange(false).emit()
                    }
                }
            }
            .map { }   // the list may be null while the mirror loads; the view scope collects non-null values
            .launchInViewScope()

        dataGateway.mixSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { event ->
                when (event) {
                    is WMMixSyncEvent.RegenerateSharePreview.Failure -> {
                        val prompt = promptFactory.previewRegenerationFailed(event.error.message)
                        eventFactory.createPromptChange(prompt).emit()
                    }
                    is WMMixSyncEvent.SetShareActive.Failure -> {
                        val prompt = promptFactory.activationFailed(event.active, event.error.message)
                        eventFactory.createPromptChange(prompt).emit()
                    }
                    else -> Unit
                }
            }
            .launchInViewScope()
    }

    override suspend fun sendInitialEvents(isFirstOnReadyCall: Boolean) = Unit   // the rows flow emits on start

    override fun extractBasePromptingPresenterAction(action: A): FSPromptingPresenter.Action.Base? = (action as? Action.ComposedSuperAction)?.wrapped

    @Suppress("UNCHECKED_CAST")
    override fun FSPrompt.Response<*>.extract(): FSPrompt.Response<P> = this as FSPrompt.Response<P>

    override fun handleNonBasePromptingPresenterAction(action: A) {
        when (action) {
            is Action.RequestRegeneratePreview -> launch {
                val uid = dataGateway.session()?.uid ?: return@launch
                dataGateway.regenerateSharePreview(uid, action.sharedMixId, createPresenterContext(action))
            }
            is Action.SetActive -> launch {
                val uid = dataGateway.session()?.uid ?: return@launch
                dataGateway.setShareActive(uid, action.sharedMixId, action.active, createPresenterContext(action))
            }
            else -> logUnknownActionType(action)
        }
    }
}

/** What the rows are built from: the mirrored shares (`null` until they arrive) and, on Pro, their statistics. */
private data class WMSharedLinksState(
    val shares: List<WMOwnedShare>?,
    val stats: List<WMShareStats>?,
    val statsVisible: Boolean
)

private fun WMOwnedShare.toRow(
    now: Instant,
    stats: Map<String, WMShareStats>?,
    statsVisible: Boolean
): WMSharedLinkRow {
    val linkStats = when {
        !statsTracked -> WMSharedLinkStats.Untracked
        !statsVisible -> WMSharedLinkStats.Locked
        stats == null -> WMSharedLinkStats.Unavailable
        else -> {
            // The statistics document starts at zero with the share; it may not have reached the mirror yet.
            val stored = stats[sharedMixId]
            WMSharedLinkStats.Shown(stored?.clickCount ?: 0, stored?.deactivatedClickCount ?: 0, stored?.lastClickedAt)
        }
    }
    return WMSharedLinkRow(
        sharedMixId = sharedMixId,
        title = title,
        badge = badge,
        hourly = hourly,
        purpose = purpose,
        shareableUrl = shareableUrl,
        createdAt = createdAt,
        previewImageUrl = previewImageUrl,
        previewState = previewState(now),
        active = active,
        stats = linkStats,
    )
}

context(eventFactory: EF, promptFactory: PF, dataGateway: G)
suspend fun <
    P: Prompt,
    E: Event<P>,
    EF: Event.Factory<P, E>,
    PF: Prompt.Factory<P>,
    G: WMSharedLinksDataGateway
> defaultSharedLinksHandlePromptResponse(response: FSPrompt.Response<P>, emit: suspend E.() -> Unit) {
    when (response.prompt) {
        is Prompt.PreviewRegenerationFailed, is Prompt.ActivationFailed -> {
            eventFactory.createPromptChange(promptFactory.none()).emit()
        }
        else -> defaultHandlePromptResponse(response, emit)
    }
}

typealias WMDefaultSharedLinksPresenter = WMSharedLinksPresenter<Prompt.Base, Event.Base<Prompt.Base>, Action.Base>

fun WMSharedLinksPresenter.Companion.createDefault(
    dataGateway: WMSharedLinksDataGateway,
    instanceId: String = FSPresenter.createInstanceId("WMSharedLinksPresenter"),
    now: () -> Instant = { Clock.System.now() }
): WMDefaultSharedLinksPresenter = object: WMBaseSharedLinksPresenter<
    Prompt.Base,
    Event.Base<Prompt.Base>,
    Action.Base,
    Event.Factory<Prompt.Base, Event.Base<Prompt.Base>>,
    Action.Factory<Prompt.Base, Action.Base>,
    Prompt.Factory<Prompt.Base>,
    WMSharedLinksDataGateway
>(
    dataGateway = dataGateway,
    eventFactory = Event.baseFactory(),
    actionFactory = Action.baseFactory(),
    promptFactory = Prompt.baseFactory(),
    cancelViewScopeOnUnready = true,
    instanceId = instanceId,
    now = now
) {}
